import Chip from '@mui/material/Chip'
import { lennarTheme } from 'src/lennarTheme'
import { getEnvVarWithDefault } from 'src/lib/env'

/**
 * Branches that say nothing about which workspace this is. Trunk is what a
 * plain checkout sits on, and a release branch is a shared line rather than a
 * piece of work in progress.
 */
const UNREMARKABLE_BRANCH = 'development'
const UNREMARKABLE_BRANCH_PREFIX = 'release/'

/**
 * Names the branch the dev server is serving (LTGC-5746).
 *
 * Several worktrees of this repo are often run at once, and two dev servers are
 * indistinguishable in the browser — it is easy to test a fix against the wrong
 * one. The badge only appears where that ambiguity exists: local development,
 * on a branch that is actually somebody's work.
 *
 * The value is captured by the `prestart` script, so a production build has no
 * branch to show even if this rendered there. Switching branches under a
 * running dev server needs a restart before the badge catches up, because
 * Create React App reads env files once at startup.
 */
export const LocalBranchIndicator: React.FC = () => {
  const branch = getEnvVarWithDefault('REACT_APP_GIT_BRANCH', '').trim()

  // A production build cannot reach this anyway — the branch is written to
  // .env.development.local, which Create React App reads only when serving in
  // development — but the guard is cheap and states the intent outright.
  // Phrased as "not production" rather than "is development" so it holds under
  // test, where NODE_ENV is neither.
  const isWorthShowing =
    process.env.NODE_ENV !== 'production' &&
    branch !== '' &&
    branch !== UNREMARKABLE_BRANCH &&
    !branch.startsWith(UNREMARKABLE_BRANCH_PREFIX)

  if (!isWorthShowing) {
    return null
  }

  return (
    <Chip
      label={branch}
      size="small"
      // Reads as a workspace label rather than as application state: this is
      // scaffolding for the developer, not something a user should act on.
      sx={{
        backgroundColor: lennarTheme?.lennarDesignSystem?.neutrals?.grey2,
        color: lennarTheme?.lennarDesignSystem?.neutrals?.grey10,
        fontFamily: 'monospace',
        ml: 2,
      }}
      title={`Local dev server running ${branch}`}
    />
  )
}

export default LocalBranchIndicator
